/**
 * Professional Admin Login Page
 * Sleek black theme with modern UI design
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, 
  User, 
  Eye, 
  EyeOff, 
  Shield, 
  ArrowRight,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import ForgotPassword from './Admin/ForgotPassword';
import adminPasswordService from '../lib/adminPasswordService';
import './AdminLogin.css';

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);
  
  // Global flag to prevent multiple redirects
  if (!window.adminRedirectInProgress) {
    window.adminRedirectInProgress = false;
  }
  
  // Global flag to prevent multiple auth checks
  if (!window.adminAuthChecked) {
    window.adminAuthChecked = false;
  }

  // Admin credentials (in production, this would be handled by a secure backend)
  const ADMIN_CREDENTIALS = {
    username: import.meta.env.VITE_ADMIN_USERNAME || 'admin',
    password: 'admin123', // This will be validated against stored credentials
    email: import.meta.env.VITE_ADMIN_EMAIL || 'akiblackcar@gmail.com'
  };


  const MAX_ATTEMPTS = 3;
  const LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    // Prevent multiple auth checks using both local and global flags
    if (hasCheckedAuth.current || window.adminAuthChecked) {
      return;
    }
    hasCheckedAuth.current = true;
    window.adminAuthChecked = true;

    // Check if user is already logged in (check both storage locations)
    const isLoggedIn = localStorage.getItem('adminLoggedIn') === 'true' || 
                      sessionStorage.getItem('admin_authenticated') === 'true';
    
    if (isLoggedIn) {
      // Use direct navigation with small delay to prevent browser throttling
      // Only redirect if we're not already on the admin page and no redirect is in progress
      if (window.location.pathname !== '/admin' && !window.adminRedirectInProgress) {
        window.adminRedirectInProgress = true;
        setTimeout(() => {
          navigate('/admin');
          // Reset flag after navigation
          setTimeout(() => {
            window.adminRedirectInProgress = false;
          }, 1000);
        }, 100);
      }
      return;
    }

    // Check for lockout status
    const lockoutData = localStorage.getItem('adminLockout');
    
    if (lockoutData) {
      try {
        const { timestamp } = JSON.parse(lockoutData);
        const remaining = timestamp + LOCKOUT_DURATION - Date.now();
        
        if (remaining > 0) {
          setIsLocked(true);
          setTimeRemaining(Math.ceil(remaining / 1000));
          startCountdown(remaining);
        } else {
          // Lockout expired, clean up
          localStorage.removeItem('adminLockout');
        }
      } catch (error) {
        console.warn('Error parsing lockout data:', error);
        localStorage.removeItem('adminLockout');
      }
    }
  }, []); // Empty dependency array - run only once

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (window.lockoutInterval) {
        clearInterval(window.lockoutInterval);
        window.lockoutInterval = null;
      }
    };
  }, []);

  const startCountdown = (duration) => {
    // Clear any existing interval
    if (window.lockoutInterval) {
      clearInterval(window.lockoutInterval);
    }
    
    window.lockoutInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(window.lockoutInterval);
          window.lockoutInterval = null;
          setIsLocked(false);
          localStorage.removeItem('adminLockout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with:', formData);
    
    if (isLocked) {
      setError(`Account locked. Please wait ${formatTime(timeRemaining)} before trying again.`);
      return;
    }

    console.log('Starting login process...');
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Simulate API call delay
    console.log('Waiting for API delay...');
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      // Validate credentials using admin password service
      console.log('Validating credentials...');
      const isValid = adminPasswordService.verifyCredentials(formData.username, formData.password);
      console.log('Credentials valid:', isValid);
      
      if (isValid) {
        console.log('Login successful, setting up session...');
        // Successful login
        setSuccess('Login successful! Redirecting...');
        localStorage.setItem('adminLoggedIn', 'true');
        localStorage.setItem('adminLoginTime', Date.now().toString());
        sessionStorage.setItem('admin_authenticated', 'true');
        sessionStorage.setItem('admin_login_time', new Date().toISOString());
        
        // Reset attempts
        setAttempts(0);
        localStorage.removeItem('adminAttempts');
        
        console.log('About to redirect to /admin');
        // Use direct navigation with small delay to prevent browser throttling
        setTimeout(() => {
          console.log('Executing navigate to /admin');
          navigate('/admin');
        }, 100);
        
      } else {
        console.log('Login failed, incrementing attempts');
        // Failed login
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('adminAttempts', newAttempts.toString());
        
        if (newAttempts >= MAX_ATTEMPTS) {
          // Lock account
          setIsLocked(true);
          setTimeRemaining(300); // 5 minutes
          localStorage.setItem('adminLockout', JSON.stringify({
            timestamp: Date.now()
          }));
          setError('Too many failed attempts. Account locked for 5 minutes.');
          startCountdown(300000);
        } else {
          setError(`Invalid credentials. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      console.log('Login process completed, setting loading to false');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="admin-login">
      {/* Background Elements */}
      <div className="login-background">
        <div className="bg-pattern"></div>
        <div className="bg-gradient"></div>
      </div>

      {/* Main Login Container */}
      <div className="login-container">
        <div className="login-card">
          {/* Header */}
          <div className="login-header">
            <div className="login-icon">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="login-title">Admin Portal</h1>
            <p className="login-subtitle">Secure access to system controls</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Username Field */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                <User className="w-4 h-4" />
                Username
              </label>
              <div className="input-container">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="form-input"
                  placeholder="Enter your username"
                  disabled={isLoading || isLocked}
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <Lock className="w-4 h-4" />
                Password
              </label>
              <div className="input-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  className="form-input"
                  placeholder="Enter your password"
                  disabled={isLoading || isLocked}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  disabled={isLoading || isLocked}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="forgot-password-container">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-link"
                disabled={isLoading || isLocked}
              >
                Forgot your password?
              </button>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="message error-message">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="message success-message">
                <CheckCircle className="w-4 h-4" />
                <span>{success}</span>
              </div>
            )}

            {/* Security Notice */}
            <div className="security-notice">
              <div className="security-item">
                <div className="security-dot"></div>
                <span>All login attempts are monitored</span>
              </div>
              <div className="security-item">
                <div className="security-dot"></div>
                <span>Account locks after 3 failed attempts</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || isLocked || !formData.username || !formData.password}
              className="login-button"
              onClick={(e) => {
                console.log('Button clicked, form data:', formData);
                console.log('Button disabled:', isLoading || isLocked || !formData.username || !formData.password);
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Authenticating...
                </>
              ) : isLocked ? (
                <>
                  <Lock className="w-4 h-4" />
                  Account Locked ({formatTime(timeRemaining)})
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <div className="footer-info">
              <div className="footer-item">
                <Shield className="w-3 h-3" />
                <span>Secure Connection</span>
              </div>
              <div className="footer-item">
                <Lock className="w-3 h-3" />
                <span>Encrypted Data</span>
              </div>
            </div>
            <p className="footer-text">
              © 2024 Aki Black Car Service. All rights reserved.
            </p>
          </div>
        </div>

      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPassword
          onClose={() => setShowForgotPassword(false)}
          onSuccess={() => setShowForgotPassword(false)}
          defaultEmail={adminPasswordService.getAdminEmail()}
        />
      )}
    </div>
  );
};

export default AdminLogin;